import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "Power Fitness Nutrition";
const origin = () => (typeof window !== "undefined" ? window.location.origin : "");
export const absUrl = (u) => (u && u.startsWith("/") ? `${origin()}${u}` : u);

// React 19 fa l'hoisting nativo di <title>/<meta>/<link> nell'<head>. Il JSON-LD lo gestiamo via effetto.
export const Seo = ({ title, description, image, noindex = false, jsonLd, type = "website", id = "page" }) => {
  const { pathname, search } = useLocation();
  const fullTitle = title ? `${title} | ${SITE}` : `${SITE} — Integratori sportivi a San Cataldo e Canicattì`;
  const desc = description || "Integratori selezionati per allenarti, recuperare e raggiungere i tuoi obiettivi. Ritiro a San Cataldo e Canicattì o consegna in 2-4 giorni.";
  const canonical = `${origin()}${pathname}${pathname === "/catalog" ? search : ""}`;
  const img = absUrl(image) || `${origin()}/hero-wheyjoy.jpg`;
  const robots = noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1";

  useEffect(() => {
    const sid = `ld-json-${id}`;
    let el = document.getElementById(sid);
    if (!jsonLd) { el?.remove(); return; }
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = sid; document.head.appendChild(el); }
    el.textContent = JSON.stringify(jsonLd);
    return () => el?.remove();
  }, [jsonLd, id]);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
    </>
  );
};

export const storeJsonLd = (info, s, base = origin()) => ({
  "@type": ["Store", "HealthAndBeautyBusiness"], "@id": `${base}/#store-${s.id}`, "name": `${SITE} ${s.label}`, "image": `${base}/hero-wheyjoy.jpg`,
  "url": `${base}/integratori-${s.id === "san_cataldo" ? "san-cataldo" : "canicatti"}`, "telephone": info.phone, "email": info.email, "priceRange": "€€",
  "address": { "@type": "PostalAddress", "streetAddress": s.address.split(",")[0], "addressLocality": s.label, "addressRegion": s.id === "san_cataldo" ? "CL" : "AG", "postalCode": s.id === "san_cataldo" ? "93017" : "92024", "addressCountry": "IT" },
  ...(s.lat && s.lng ? { "geo": { "@type": "GeoCoordinates", "latitude": s.lat, "longitude": s.lng } } : {}),
  "hasMap": s.maps_url || `https://maps.google.com/?q=${encodeURIComponent(s.address)}`,
  "areaServed": [{ "@type": "AdministrativeArea", "name": "Sicilia" }, { "@type": "AdministrativeArea", "name": s.id === "san_cataldo" ? "Provincia di Caltanissetta" : "Provincia di Agrigento" }],
  "currenciesAccepted": "EUR", "paymentAccepted": "Contanti, Carta, PayPal, Klarna, Bonifico",
  ...(s.hours ? { "openingHours": s.hours } : {}),
  ...(info.google_rating ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": info.google_rating, "reviewCount": info.google_reviews_count || 1 } } : {}),
});

export const faqJsonLd = (faqs) => ({
  "@type": "FAQPage",
  "mainEntity": faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
});

export const localBusinessJsonLd = (info) => {
  if (!info) return null;
  const base = origin();
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite", "@id": `${base}/#website`, "url": base, "name": SITE, "inLanguage": "it-IT",
        "potentialAction": { "@type": "SearchAction", "target": `${base}/catalog?q={search_term_string}`, "query-input": "required name=search_term_string" },
      },
      {
        "@type": "Organization", "@id": `${base}/#org`, "name": info.business_name || SITE, "url": base, "logo": `${base}/icons/icon-512.png`,
        "email": info.email, "telephone": info.phone, "vatID": info.vat, "areaServed": { "@type": "AdministrativeArea", "name": "Sicilia" },
        "sameAs": [info.instagram && `https://instagram.com/${info.instagram}`, info.tiktok && `https://tiktok.com/@${info.tiktok}`, info.google_place_url].filter(Boolean),
      },
      ...(info.stores || []).map((s) => storeJsonLd(info, s, base)),
    ],
  };
};

export const productJsonLd = (p, meta) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": p.name,
  "image": [absUrl(p.image_url)],
  "description": p.description || `${p.name} — ${p.brand || SITE}. Disponibile online e nei negozi Power Fitness Nutrition di San Cataldo e Canicattì.`,
  "brand": { "@type": "Brand", "name": p.brand || SITE },
  "sku": p.product_id,
  ...(p.barcode ? { "gtin13": p.barcode } : {}),
  ...(meta?.rating_count > 0 ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": meta.rating_avg, "reviewCount": meta.rating_count, "bestRating": 5, "worstRating": 1 } } : {}),
  "offers": {
    "@type": "Offer", "url": `${origin()}/product/${p.product_id}`, "priceCurrency": "EUR", "price": Number(p.price).toFixed(2),
    "availability": meta?.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
    "itemCondition": "https://schema.org/NewCondition",
    "seller": { "@type": "Organization", "name": SITE },
  },
});

export const articleJsonLd = (post) => ({
  "@type": "Article",
  "headline": post.title,
  "description": post.excerpt,
  "image": post.cover_image ? [absUrl(post.cover_image)] : undefined,
  "datePublished": post.published_at,
  "dateModified": post.updated_at || post.published_at,
  "inLanguage": "it-IT",
  "keywords": (post.tags || []).join(", "),
  "author": { "@type": "Organization", "name": SITE, "url": origin() },
  "publisher": { "@type": "Organization", "name": SITE, "logo": { "@type": "ImageObject", "url": `${origin()}/icons/icon-512.png` } },
  "mainEntityOfPage": `${origin()}/blog/${post.slug}`,
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({ "@type": "ListItem", "position": i + 1, "name": it.name, "item": `${origin()}${it.path}` })),
});
